import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loading, Notify } from 'notiflix';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import BasicInfoStep from './steps/BasicInfoStep';
import CategoriesStep from './steps/CategoriesStep';
import TiersStep from './steps/TiersStep';
import { 
  setEditMode, 
  fetchValuePackDetails, 
  resetForm, 
  setCurrentStep,
  nextStep, 
  prevStep, 
  updateValuePack, 
  createValuePack,
  selectLoading,
  selectSubmitting,
  selectCurrentOperation,
  selectError,
  selectCurrentStep,
  fetchCategoriesOptimized,
  setFormData,
  clearFormErrors,
  setFormErrors
} from 'store/slices/valuePackSlice';
import { useValuePackForm } from 'hooks/useValuePackForm';
import { getValidationSchemaByStep } from 'schemas/valuePackSchema';
import { STEP_LABELS } from 'config/valuePackFormConfig';
import { FORM_STEPS } from 'config/valuePackFormConfig';

// Unified fieldDisplayNames for all error messages
const fieldDisplayNames = {
  'name': 'Value Pack Name',
  'subTitle': 'Sub Title', 
  'slug': 'URL Slug',
  'shortDescription': 'Short Description',
  'description': 'Full Description',
  'tags': 'Tags',
  'imageUrl': 'Main Image',
  'bannerImageUrl': 'Banner Image',
  'publishedAt': 'Publish Date',
  'expiresAt': 'Expiry Date',
  'categories': 'Categories',
  'targetIndustries': 'Target Industries',
  'tiers': 'Pricing Tiers',
  'durationId': 'Duration',
  'countryId': 'Country',
  // For backward compatibility
  'selectedDuration': 'Duration',
  'selectedCountry': 'Country'
};

const MultiStepValuePackForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const formRef = useRef(null);
  // When a failed final submit jumps to the step that actually has the error,
  // skip the "step changed" cleanup below once so that step's validation stays
  // triggered and its real field-level errors render instead of being cleared.
  const suppressClearOnStepChangeRef = useRef(false);
  
  // Use specific selectors instead of entire state
  const currentStep = useSelector(selectCurrentStep);
  const loading = useSelector(selectLoading);
  const submitting = useSelector(selectSubmitting);
  const currentOperation = useSelector(selectCurrentOperation);
  const error = useSelector(selectError);
  
  const categories = useSelector((state) => state.valuePack.categories);
  const formErrors = useSelector((state) => state.valuePack.formErrors);

  const { 
    validateCurrentStep, 
    getAllValues,
    getFieldValue,
    loading: formLoading,
    clearErrors
  } = useValuePackForm(currentStep, Boolean(id));

  const isEdit = Boolean(id);
  const tiers = getFieldValue('tiers') || [];
  
  const [stepValidationStates, setStepValidationStates] = useState({
    [FORM_STEPS.BASIC_INFO]: false,
    [FORM_STEPS.CATEGORIES]: false,  
    [FORM_STEPS.PRICING_TIERS]: false 
  });

  const [validatedSteps, setValidatedSteps] = useState(new Set());

  const isInitialLoading = loading && currentOperation === 'fetchValuePackDetails';
  const isSubmittingForm = submitting && ['createValuePack', 'updateValuePack'].includes(currentOperation);
  const isCategoriesLoading = loading && ['fetchCategoriesOptimized', 'fetchCategories'].includes(currentOperation);

  const preloadCategoriesWithFeatures = useCallback(async () => {
    if (categories?.length === 0 && !isCategoriesLoading) {
      try {
        await dispatch(fetchCategoriesOptimized()).unwrap();
      } catch (error) {
        console.warn("Failed to preload categories with features:", error);
      }
    }
  }, [dispatch, categories?.length, isCategoriesLoading]);

  // Value pack id the edit form was filled for. Saving an edit updates the
  // pack in the store, which would otherwise refill the form from the save
  // response while the user stays on the page.
  const initializedForIdRef = useRef(null);

  // Initialize form
  useEffect(() => {
    const initializeForm = async () => {
      try {
        if (isEdit && id) {
          if (initializedForIdRef.current === id) return;
          initializedForIdRef.current = id;
          dispatch(setEditMode(true));
          
          // The list only carries minimal rows, so the complete pack is fetched
          // on demand. Its categories are full objects; normalize to plain IDs so
          // it matches the shape CategoriesStep expects (and produces from create).
          const pack = await dispatch(fetchValuePackDetails(id)).unwrap();
          dispatch(setFormData({
            ...pack,
            categories: Array.isArray(pack.categories)
              ? pack.categories.map((cat) =>
                  cat && typeof cat === 'object' ? cat.id : cat
                )
              : [],
          }));
          // If editing, mark all steps as validated
          setValidatedSteps(new Set([0, 1, 2]));

        } else {
          initializedForIdRef.current = null;
          dispatch(resetForm());
          dispatch(setEditMode(false));
          // For new forms, only mark the first step as accessible
          setValidatedSteps(new Set([0]));
        }

        // Preload categories in background for better UX
        preloadCategoriesWithFeatures();

      } catch (error) {
        Notify.failure('Failed to load form data');
        navigate('/value-pack');
      }
    };

    initializeForm();
  }, [dispatch, id, isEdit, navigate, preloadCategoriesWithFeatures]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
  }, [dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      Notify.failure(error);
      if (error.includes('not found')) {
        navigate('/value-pack');
      }
    }
  }, [error, navigate]);

  // Loading indicator
  useEffect(() => {
    if (isInitialLoading && isEdit) {
      Loading.circle("Loading value pack data...");
    } else {
      Loading.remove();
    }
    
    return () => {
      Loading.remove();
    };
  }, [isInitialLoading, isEdit]);

  // Clear errors and reset validation on step change
  useEffect(() => {
    if (suppressClearOnStepChangeRef.current) {
      suppressClearOnStepChangeRef.current = false;
      return;
    }
    dispatch(clearFormErrors());
    setStepValidationStates(prev => ({
      ...prev,
      [currentStep]: false
    }));
  }, [currentStep, dispatch]);

  const scrollToFirstError = useCallback(() => {
    setTimeout(() => {
      const firstErrorField = formRef.current?.querySelector('.Mui-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  }, []);

  // Validates a specific step's own schema against the given data, regardless of
  // which step is currently active. `validateStep` from useStepValidation is bound
  // to whichever step was current when the hook rendered, so it can't be reused
  // across steps - each step must resolve and run its own schema here.
  const validateStepSchema = useCallback(async (stepIndex, data) => {
    const stepSchema = getValidationSchemaByStep(stepIndex, isEdit);
    try {
      await stepSchema.validate(data, { abortEarly: false });
      return { isValid: true, errors: {} };
    } catch (validationError) {
      const errors = {};
      if (validationError.inner && validationError.inner.length > 0) {
        validationError.inner.forEach((err) => {
          errors[err.path] = { message: err.message };
        });
      } else {
        errors[validationError.path || 'general'] = { message: validationError.message };
      }
      return { isValid: false, errors };
    }
  }, [isEdit]);

  const validateStepData = useCallback(async (stepIndex) => {
    try {
      const formValues = getAllValues();
      const { isValid } = await validateStepSchema(stepIndex, formValues);
      return isValid;
    } catch (error) {
      return false;
    }
  }, [getAllValues, validateStepSchema]);

  const handleNext = useCallback(async () => {
    try {
      // Set validation for current step
      setStepValidationStates(prev => ({
        ...prev,
        [currentStep]: true
      }));
      
      // Give some time for validation to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const isStepValid = await validateCurrentStep();      
      if (isStepValid) {
        // Mark current step as validated
        setValidatedSteps(prev => {
          const newSet = new Set(prev);
          newSet.add(currentStep);
          return newSet;
        });
        
        // Reset validation for current step when moving to next
        setStepValidationStates(prev => ({
          ...prev,
          [currentStep]: false
        }));
        dispatch(nextStep());
      } else {
        if (formErrors && Object.keys(formErrors).length > 0) {
          // Create user-friendly error messages
          const errorMessages = Object.entries(formErrors)
            .filter(([field, error]) => error && error.message)
            .map(([field, error]) => {
              const fieldName = fieldDisplayNames[field] || field;
              return `${fieldName}: ${error.message}`;
            });
          
          if (errorMessages.length > 0) {
            // Show first 2 errors
            let displayMessage;
            if (errorMessages.length <= 2) {
              displayMessage = errorMessages.join(', ');
            } else {
              displayMessage = `${errorMessages.slice(0, 2).join(', ')} and ${errorMessages.length - 2} more errors`;
            }
            
            Notify.failure(`Please fix: ${displayMessage}`);
          } else {
            Notify.failure('Please fix the validation errors before proceeding');
          }
        } else if (formErrors.tiers && currentStep === FORM_STEPS.PRICING_TIERS && isEdit) {
          Notify.failure('You cannot update value pack without creating tiers first. Please add at least one pricing tier.');
        } else {
          Notify.failure('Please fix the validation errors before proceeding');
        }
        
        scrollToFirstError();
      }
    } catch (error) {  
      // Handle validation errors in catch block
      if (formErrors && Object.keys(formErrors).length > 0) {
        const errorMessages = Object.entries(formErrors)
          .filter(([field, error]) => error && error.message)
          .map(([field, error]) => {
            const fieldName = fieldDisplayNames[field] || field;
            return `${fieldName}: ${error.message}`;
          });
        
        if (errorMessages.length > 0) {
          Notify.failure(`Please fix: ${errorMessages.slice(0, 2).join(', ')}`);
        } else {
          Notify.failure('Please check your input and try again');
        }
      } else if (formErrors.tiers && currentStep === FORM_STEPS.PRICING_TIERS && isEdit) {
        Notify.failure('You cannot update value pack without creating tiers first. Please add at least one pricing tier.');
      } else {
        Notify.failure('Please check your input and try again');
      }
      
      scrollToFirstError();
    }
  }, [validateCurrentStep, dispatch, scrollToFirstError, currentStep, formErrors, isEdit]); 

  const handlePrev = useCallback(() => {
    dispatch(prevStep());
  }, [dispatch]);

  const handleStepClick = useCallback(async (stepIndex) => {
    // Don't allow navigating to the same step
    if (stepIndex === currentStep) return;
    
    // EDIT MODE: Allow direct navigation to any step without validation
    if (isEdit) {
      dispatch(setCurrentStep(stepIndex));
      return;
    }
    
    // CREATE MODE: Original validation logic
    // Allow navigating to previous steps that are already validated
    if (stepIndex < currentStep && validatedSteps.has(stepIndex)) {
      dispatch(setCurrentStep(stepIndex));
      return;
    }
    
    // For navigating to future steps, validate all previous steps first
    if (stepIndex > currentStep) {
      let allPreviousStepsValid = true;
      
      // Validate all steps from current to the target step
      for (let i = currentStep; i < stepIndex; i++) {
        const isValid = await validateStepData(i);
        if (!isValid) {
          allPreviousStepsValid = false;
          // Navigate to the first invalid step
          dispatch(setCurrentStep(i));
          setStepValidationStates(prev => ({
            ...prev,
            [i]: true
          }));
          scrollToFirstError();
          Notify.failure(`Please complete step ${i + 1} before proceeding to step ${stepIndex + 1}`);
          break;
        }
      }
      
      if (allPreviousStepsValid) {
        // Mark all steps as validated
        setValidatedSteps(prev => {
          const newSet = new Set(prev);
          for (let i = currentStep; i < stepIndex; i++) {
            newSet.add(i);
          }
          return newSet;
        });
        
        dispatch(setCurrentStep(stepIndex));
      }
    }
  }, [currentStep, validatedSteps, validateStepData, dispatch, scrollToFirstError, isEdit]);
  
  const CustomStepLabel = ({ index, label }) => {
    const isCompleted = validatedSteps.has(index);
    const isAccessible = isEdit ? true : (isCompleted || index === 0);
    const isCurrent = index === currentStep;
    
    if (!isAccessible && !isCurrent && !isEdit) {
      return (
        <Tooltip title="Complete previous steps to access this step">
          <StepLabel 
            icon={<LockIcon color="disabled" />}
            sx={{ 
              cursor: 'not-allowed',
              '& .MuiStepLabel-label': {
                color: 'text.disabled'
              }
            }}
          >
            {label}
          </StepLabel>
        </Tooltip>
      );
    }
    
    return (
      <StepLabel 
        onClick={() => isAccessible && handleStepClick(index)}
        sx={{ 
          cursor: isAccessible ? 'pointer' : 'default',
          '& .MuiStepLabel-label': {
            color: index <= currentStep ? 'primary.main' : 'text.secondary'
          }
        }}
      >
        {label}
      </StepLabel>
    );
  };

  const handleFormSubmit = useCallback(async () => {
    try {
      // Set validation for all steps
      setStepValidationStates({
        [FORM_STEPS.BASIC_INFO]: true,
        [FORM_STEPS.CATEGORIES]: true,
        [FORM_STEPS.PRICING_TIERS]: true     
      });
      
      // Give some time for validation to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const formValues = getAllValues();
      
      // Extract durationId and countryId for backend
      const processedValues = {
        ...formValues,
        durationId: formValues.durationId?.id || formValues.selectedDuration?.id || formValues.durationId || formValues.selectedDuration,
        countryId: formValues.countryId?.id || formValues.selectedCountry?.id || formValues.countryId || formValues.selectedCountry
      };

      // Remove old field names
      delete processedValues.selectedDuration;
      delete processedValues.selectedCountry;

      // Validate every step against its own schema (not just the currently active one)
      let hasErrors = false;
      let firstInvalidStep = 0;
      let firstInvalidErrors = {};

      for (let step = 0; step < STEP_LABELS.length; step++) {
        const { isValid, errors } = await validateStepSchema(step, processedValues);
        if (!isValid) {
          hasErrors = true;
          firstInvalidStep = step;
          firstInvalidErrors = errors;
          break;
        }
      }

      if (hasErrors) {
        // Keep this step's own validation "triggered" across the jump so its
        // real field-level errors render instead of being cleared by the
        // step-change cleanup effect.
        suppressClearOnStepChangeRef.current = firstInvalidStep !== currentStep;
        dispatch(setCurrentStep(firstInvalidStep));
        dispatch(setFormErrors(firstInvalidErrors));
        scrollToFirstError();
        const errorFieldNames = Object.keys(firstInvalidErrors)
          .map((field) => fieldDisplayNames[field] || field)
          .join(', ');
        Notify.failure(
          `Please fix the following on "${STEP_LABELS[firstInvalidStep]}": ${errorFieldNames || 'validation errors'}`
        );
        return;
      }
      
      if (isEdit) {
        await dispatch(updateValuePack({ id, valuePackData: processedValues })).unwrap();
        // Stay on the form after an edit: only the toast confirms the save
        Notify.success('Value pack updated successfully!');
      } else {
        await dispatch(createValuePack(processedValues)).unwrap();
        Notify.success('Value pack created successfully! You can now add pricing tiers.');
        navigate('/value-pack');
      }
    } catch (error) {
      Notify.failure(error?.message || 'An error occurred while saving');
    }
  }, [getAllValues, validateStepSchema, isEdit, dispatch, id, navigate, scrollToFirstError, currentStep]);

  const renderStepContent = useMemo(() => {
    switch (currentStep) {
      case FORM_STEPS.BASIC_INFO:
        return (
          <BasicInfoStep 
            stepIndex={currentStep} 
            isEdit={isEdit} 
            validationTriggered={stepValidationStates[FORM_STEPS.BASIC_INFO]} 
          />
        );
      case FORM_STEPS.CATEGORIES:
        return (
          <CategoriesStep 
            stepIndex={currentStep} 
            isEdit={isEdit} 
            validationTriggered={stepValidationStates[FORM_STEPS.CATEGORIES]} 
          />
        );
      case FORM_STEPS.PRICING_TIERS: 
        return (
          <TiersStep 
            stepIndex={currentStep} 
            isEdit={isEdit} 
            validationTriggered={stepValidationStates[FORM_STEPS.PRICING_TIERS]} 
          />
        );
      default:
        return (
          <BasicInfoStep 
            stepIndex={0} 
            isEdit={isEdit} 
            validationTriggered={stepValidationStates[FORM_STEPS.BASIC_INFO]} 
          />
        );
    }
  }, [currentStep, isEdit, stepValidationStates]);

  if (isInitialLoading && isEdit) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Loading value pack data...
            </Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={6} pb={3} ref={formRef}>
        <Card>
          <CardHeader
            title={
              <Box display="flex" alignItems="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/value-pack')}
                  sx={{ mr: 2 }}
                  disabled={isSubmittingForm}
                >
                  Back
                </Button>
                <Typography variant="h6">
                  {isEdit ? 'Edit Value Pack' : 'Create New Value Pack'}
                </Typography>

                {isEdit && currentStep === FORM_STEPS.PRICING_TIERS && (!tiers || tiers.length === 0) && (
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    ml={2}
                    sx={{ 
                      bgcolor: 'warning.light', 
                      color: 'warning.dark',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      ⚠️ Add Tiers to Update
                    </Typography>
                  </Box>
                )}

                {/* Show preloading indicator */}
                {isCategoriesLoading && (
                  <Box display="flex" alignItems="center" ml={2}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      Loading categories...
                    </Typography>
                  </Box>
                )}
              </Box>
            }
            subheader={isEdit ? 
              'Update the value pack details and save changes' : 
              'Combine multiple categories into a discounted package. Categories with features are loaded automatically.'
            }
          />
          
          <CardContent>
            {/* Step Indicator */}
            {formErrors && Object.keys(formErrors).length > 0 && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => dispatch(clearFormErrors())}
              >
                <Typography variant="h6" gutterBottom>
                  Validation Errors:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {Object.entries(formErrors).map(([field, error]) => {
                    const fieldName = fieldDisplayNames[field] || field;
                    return (
                      <li key={field}>
                        <Typography variant="body2">
                          <strong>{fieldName}:</strong> {error.message || 'Unknown error'}
                        </Typography>
                      </li>
                    );
                  })}
                </Box>
              </Alert>
            )}

            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Stepper activeStep={currentStep} alternativeLabel>
                {STEP_LABELS.map((label, index) => (
                  <Step key={label}>
                    <CustomStepLabel index={index} label={label} />
                    {/* Show loading indicator for categories step */}
                    {index === FORM_STEPS.CATEGORIES && isCategoriesLoading && (
                      <CircularProgress size={12} sx={{ ml: 0.5 }} />
                    )}
                  </Step>
                ))}
              </Stepper>
            </Paper>

            {/* Step Content */}
            <Box minHeight="400px">
              {renderStepContent}
            </Box>

            {/* Navigation Buttons */}
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button
                disabled={currentStep === 0 || isSubmittingForm}
                onClick={handlePrev}
                sx={{ color: "#495057" }}
                startIcon={<PrevIcon />}
              >
                Previous
              </Button>

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  sx={{ color: "#51baefff" }} 
                  onClick={() => navigate('/value-pack')}
                  disabled={isSubmittingForm}
                >
                  Cancel
                </Button>

                {currentStep === STEP_LABELS.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleFormSubmit}
                    startIcon={isSubmittingForm ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{ color: "#fff" }}
                    disabled={isSubmittingForm || (isEdit && (!tiers || tiers.length === 0))}
                  >
                    {isSubmittingForm ? (
                      `${isEdit ? 'Updating' : 'Creating'}...`
                    ) : (
                      `${isEdit ? 'Update' : 'Create'} Value Pack`
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NextIcon />}
                    sx={{ color: "#fff" }} 
                    disabled={isSubmittingForm}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>

            {/* Show global submission indicator */}
            {isSubmittingForm && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  {currentOperation === 'createValuePack' ? 'Creating value pack...' : 'Updating value pack...'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default MultiStepValuePackForm;
